import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  .dca-root {
    --accent:       #F2A900;
    --accent-strong:#E89100;
    --accent-soft:  rgba(242,169,0,0.08);
    --accent-line:  rgba(242,169,0,0.15);
    --bg:           #0E0E0C;
    --surface:      #161614;
    --surface-2:    #1A1A17;
    --border:       #2A2A26;
    --border-strong:#3A3A35;
    --divider:      #222220;
    --fg:           #F0EFE9;
    --fg-2:         #C8C7BF;
    --muted:        #7A7971;
    --muted-2:      #4A4A44;
    --pos:          #3EAD7D;
    --pos-soft:     #0D2B1F;
    --neg:          #D95B40;
    --neg-soft:     #2E1009;
    --mono: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
    --sans: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
    --radius: 4px; --radius-lg: 8px;
    font-family: var(--sans); font-size: 13px; line-height: 1.5;
    color: var(--fg); -webkit-font-smoothing: antialiased; padding: 0 0 80px;
  }
  .dca-topbar { display:flex; align-items:center; justify-content:space-between; padding:14px 0 16px; border-bottom:1px solid var(--border); margin-bottom:24px; }
  .dca-brand { display:flex; align-items:center; gap:12px; }
  .dca-brand-mark { width:28px; height:28px; background:var(--accent); border-radius:6px; display:grid; place-items:center; color:white; font-weight:700; font-family:var(--mono); font-size:15px; }
  .dca-brand-name { font-weight:600; font-size:14px; color:var(--fg); }
  .dca-brand-sub  { color:var(--muted); font-size:11px; font-family:var(--mono); }
  .dca-topbar-actions { display:flex; gap:8px; align-items:center; }
  .dca-btn { font-family:var(--sans); font-size:12px; font-weight:500; padding:7px 12px; border-radius:var(--radius); border:1px solid var(--border-strong); background:var(--surface); color:var(--fg); cursor:pointer; transition:all 0.12s; display:inline-flex; align-items:center; gap:6px; }
  .dca-btn:hover { border-color:var(--fg-2); }
  .dca-btn-primary { background:var(--accent); border-color:var(--accent-strong); color:#fff; }
  .dca-btn-primary:hover { background:var(--accent-strong); }
  .dca-section { display:flex; align-items:baseline; gap:8px; margin:28px 0 12px; }
  .dca-num-badge { font-family:var(--mono); font-size:10px; font-weight:600; background:var(--fg); color:var(--bg); padding:2px 6px; border-radius:3px; letter-spacing:0.04em; }
  .dca-section h2 { margin:0; font-size:13px; font-weight:600; color:var(--fg); }
  .dca-dots { flex:1; border-bottom:1px dashed var(--border-strong); margin:0 4px 4px; }
  .dca-hint { color:var(--muted); font-family:var(--mono); font-size:11px; }
  .dca-hero { display:grid; grid-template-columns:340px 1fr; gap:16px; }
  .dca-pnl { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius-lg); padding:20px 22px 22px; display:flex; flex-direction:column; gap:16px; }
  .dca-pnl-head { display:flex; align-items:center; justify-content:space-between; color:var(--muted); font-size:11px; letter-spacing:0.06em; text-transform:uppercase; font-weight:500; }
  .dca-live { display:inline-flex; align-items:center; gap:5px; color:var(--pos); text-transform:none; letter-spacing:0; font-family:var(--mono); font-size:10px; }
  .dca-live-dot { width:6px; height:6px; border-radius:50%; background:var(--pos); box-shadow:0 0 0 3px rgba(62,173,125,0.18); animation:dcaPulse 1.8s infinite; }
  @keyframes dcaPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  .dca-pnl-value { font-family:var(--mono); font-size:38px; font-weight:500; letter-spacing:-0.025em; line-height:1; color:var(--fg); }
  .dca-pnl-value .dca-cur { font-size:17px; color:var(--muted); margin-right:5px; font-weight:400; }
  .dca-pnl-delta { display:inline-flex; align-items:center; gap:8px; margin-top:4px; }
  .dca-chip { font-family:var(--mono); font-size:12px; padding:3px 8px; border-radius:3px; font-weight:500; }
  .dca-chip.pos { background:var(--pos-soft); color:var(--pos); }
  .dca-chip.neg { background:var(--neg-soft); color:var(--neg); }
  .dca-pnl-split { display:grid; grid-template-columns:1fr 1fr; border-top:1px solid var(--divider); padding-top:14px; }
  .dca-pnl-split > div { padding-right:12px; }
  .dca-pnl-split > div + div { padding-right:0; padding-left:16px; border-left:1px solid var(--divider); }
  .dca-lbl { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:0.04em; margin-bottom:4px; }
  .dca-val { font-family:var(--mono); font-size:15px; font-weight:500; }
  .dca-pnl-meta { font-family:var(--mono); font-size:11px; color:var(--muted); }
  .dca-chart-card { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius-lg); display:flex; flex-direction:column; overflow:hidden; }
  .dca-chart-head { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid var(--divider); flex-wrap:wrap; gap:8px; }
  .dca-chart-tabs { display:flex; gap:2px; flex-wrap:wrap; }
  .dca-chart-tab { background:transparent; border:none; padding:5px 10px; border-radius:var(--radius); font-size:12px; color:var(--muted); cursor:pointer; font-weight:500; }
  .dca-chart-tab:hover { color:var(--fg); background:var(--divider); }
  .dca-chart-tab.active { background:var(--fg); color:var(--bg); }
  .dca-tf-wrap { display:flex; border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; }
  .dca-tf-btn { background:var(--surface); border:none; border-right:1px solid var(--border); padding:4px 9px; font-family:var(--mono); font-size:11px; color:var(--muted); cursor:pointer; }
  .dca-tf-btn:last-child { border-right:none; }
  .dca-tf-btn.active { background:var(--fg); color:var(--bg); }
  .dca-tf-btn:hover:not(.active) { background:var(--divider); color:var(--fg); }
  .dca-chart-body { position:relative; padding:14px 18px 10px; height:280px; }
  .dca-chart-legend { display:flex; gap:14px; margin-bottom:4px; font-family:var(--mono); font-size:11px; color:var(--muted); }
  .dca-legend-item { display:inline-flex; align-items:center; gap:6px; }
  .dca-swatch { width:10px; height:10px; border-radius:2px; }
  .dca-tooltip { position:absolute; pointer-events:none; background:var(--fg); color:var(--bg); padding:8px 10px; border-radius:4px; font-family:var(--mono); font-size:11px; white-space:nowrap; box-shadow:0 4px 12px rgba(0,0,0,0.4); z-index:5; }
  .dca-tooltip .dca-t-date { border-bottom:1px solid rgba(255,255,255,0.12); padding-bottom:4px; margin-bottom:4px; }
  .dca-tooltip .dca-t-row { display:flex; justify-content:space-between; gap:14px; }
  .dca-tooltip .dca-t-lbl { color:rgba(255,255,255,0.45); }
  .dca-stats { display:grid; grid-template-columns:repeat(5,1fr); gap:1px; background:var(--border); border:1px solid var(--border); border-radius:var(--radius-lg); overflow:hidden; }
  .dca-stat { background:var(--surface); padding:16px 18px 18px; display:flex; flex-direction:column; gap:6px; position:relative; }
  .dca-stat-lbl { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:0.05em; font-weight:500; }
  .dca-stat-val { font-family:var(--mono); font-size:20px; font-weight:500; letter-spacing:-0.015em; line-height:1.1; }
  .dca-stat-val .dca-sub { color:var(--muted); font-size:12px; font-weight:400; margin-left:4px; }
  .dca-stat-foot { font-family:var(--mono); font-size:11px; color:var(--muted); }
  .dca-spark { display:block; margin-top:6px; opacity:0.75; }
  .dca-goals { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-top:14px; }
  .dca-goal { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius-lg); padding:18px 20px; }
  .dca-goal-top { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px; }
  .dca-goal-top h3 { margin:0; font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--fg-2); }
  .dca-goal-pct { font-family:var(--mono); font-size:20px; font-weight:500; }
  .dca-goal-bar { height:10px; background:var(--divider); border-radius:2px; overflow:hidden; }
  .dca-goal-fill { height:100%; background:var(--accent); border-radius:2px; transition:width 0.6s cubic-bezier(0.2,0.9,0.3,1); }
  .dca-goal-fill.alt { background:var(--fg); }
  .dca-goal-ticks { display:flex; justify-content:space-between; font-family:var(--mono); font-size:10px; color:var(--muted); margin-top:6px; }
  .dca-goal-ticks .dca-editable { cursor:pointer; border-bottom:1px dashed transparent; }
  .dca-goal-ticks .dca-editable:hover { border-bottom-color:var(--muted); }
  .dca-goal-ticks input { font-family:var(--mono); background:var(--surface-2); border:1px solid var(--accent); border-radius:4px; padding:1px 4px; width:100px; color:var(--fg); font-size:10px; }
  .dca-records { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius-lg); overflow:hidden; margin-top:14px; }
  .dca-records-toolbar { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-bottom:1px solid var(--divider); gap:10px; flex-wrap:wrap; }
  .dca-search { display:inline-flex; align-items:center; gap:6px; background:var(--surface-2); border:1px solid var(--border); border-radius:var(--radius); padding:5px 10px; font-family:var(--mono); font-size:12px; width:220px; }
  .dca-search input { border:none; outline:none; background:transparent; font-family:var(--mono); font-size:12px; flex:1; color:var(--fg); }
  .dca-select { font-family:var(--mono); font-size:12px; padding:5px 8px; background:var(--surface); border:1px solid var(--border); border-radius:var(--radius); color:var(--fg); }
  .dca-records-scroll { overflow-x:auto; }
  table.dca-table { width:100%; border-collapse:collapse; font-family:var(--mono); font-size:12px; }
  .dca-table th,.dca-table td { padding:9px 12px; text-align:right; white-space:nowrap; border-bottom:1px solid var(--divider); }
  .dca-table th:first-child,.dca-table td:first-child { text-align:left; }
  .dca-table thead th { background:var(--surface-2); color:var(--muted); font-weight:500; font-size:10px; text-transform:uppercase; letter-spacing:0.06em; border-bottom:1px solid var(--border); cursor:pointer; user-select:none; }
  .dca-table thead th:hover { color:var(--fg); }
  .dca-sort-arrow { display:inline-block; margin-left:4px; opacity:0.4; }
  .dca-table thead th.dca-sorted .dca-sort-arrow { opacity:1; color:var(--accent); }
  .dca-table tbody tr { transition:background 0.08s; }
  .dca-table tbody tr:hover { background:var(--surface-2); }
  .dca-table td.pos { color:var(--pos); }
  .dca-table td.neg { color:var(--neg); }
  .dca-day-chip { display:inline-block; background:var(--divider); color:var(--fg-2); padding:1px 6px; border-radius:3px; font-size:11px; min-width:28px; text-align:center; }
  .dca-platform-badge { display:inline-block; background:var(--surface-2); color:var(--muted); padding:1px 6px; border-radius:3px; font-size:10px; border:1px solid var(--border); }
  .dca-table tbody tr td.dca-row-actions { width:60px; text-align:right; padding-right:8px; opacity:0; transition:opacity 120ms; }
  .dca-table tbody tr:hover td.dca-row-actions { opacity:1; }
  .dca-row-actions button { background:transparent; border:none; cursor:pointer; padding:4px; color:var(--muted); opacity:0.6; }
  .dca-row-actions button:hover { opacity:1; color:var(--fg); }
  .dca-row-actions button.danger:hover { color:var(--neg); }
  .dca-row-edit input { width:100%; background:var(--surface); border:1px solid var(--accent); border-radius:4px; padding:4px 6px; font-family:var(--mono); font-size:12px; color:var(--fg); }
  .dca-pagination { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; border-top:1px solid var(--divider); font-family:var(--mono); font-size:12px; color:var(--muted); }
  .dca-pager { display:flex; gap:4px; }
  .dca-pager button { background:var(--surface); border:1px solid var(--border); font-family:var(--mono); font-size:12px; padding:4px 9px; border-radius:3px; cursor:pointer; min-width:28px; color:var(--fg-2); }
  .dca-pager button:hover:not(:disabled) { background:var(--divider); }
  .dca-pager button.active { background:var(--fg); color:var(--bg); border-color:var(--fg); }
  .dca-pager button:disabled { opacity:0.35; cursor:not-allowed; }
  .dca-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.65); backdrop-filter:blur(4px); display:grid; place-items:center; z-index:80; }
  .dca-modal { background:var(--surface); border:1px solid var(--border); border-radius:var(--radius-lg); width:460px; max-width:calc(100vw - 32px); box-shadow:0 20px 60px rgba(0,0,0,0.5); overflow:hidden; }
  .dca-modal-head { padding:16px 20px; border-bottom:1px solid var(--divider); display:flex; justify-content:space-between; align-items:center; }
  .dca-modal-head h3 { margin:0; font-size:14px; font-weight:600; }
  .dca-modal-body { padding:20px; display:flex; flex-direction:column; gap:14px; }
  .dca-field { display:flex; flex-direction:column; gap:5px; }
  .dca-field label { font-size:11px; color:var(--muted); text-transform:uppercase; letter-spacing:0.05em; font-weight:500; }
  .dca-field input,.dca-field select,.dca-field textarea { font-family:var(--mono); font-size:14px; padding:9px 11px; border:1px solid var(--border); border-radius:var(--radius); background:var(--surface-2); color:var(--fg); outline:none; width:100%; box-sizing:border-box; }
  .dca-field textarea { font-size:13px; resize:vertical; min-height:64px; font-family:var(--sans); }
  .dca-field input:focus,.dca-field select:focus,.dca-field textarea:focus { border-color:var(--accent); }
  .dca-field-pair { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .dca-preview-row { background:var(--surface-2); border:1px dashed var(--border-strong); border-radius:var(--radius); padding:10px 12px; font-family:var(--mono); font-size:11px; color:var(--muted); display:flex; justify-content:space-between; }
  .dca-preview-row strong { color:var(--fg); font-weight:500; }
  .dca-modal-foot { padding:12px 20px; border-top:1px solid var(--divider); display:flex; justify-content:flex-end; gap:8px; }
  .dca-err { color:var(--neg); font-size:12px; }
  .dca-spinner { width:26px; height:26px; border-radius:50%; border:2.5px solid var(--border); border-top-color:var(--accent); animation:dcaSpin 0.7s linear infinite; margin:60px auto; }
  @keyframes dcaSpin { to { transform:rotate(360deg); } }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtInt = (n) => Math.round(n).toLocaleString("en-US");
const fmtThb = (n, d = 2) =>
  Number(n).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
const fmtPct = (n, d = 2) => (n >= 0 ? "+" : "") + Number(n).toFixed(d) + "%";
const fmtDate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};
const fmtDateShort = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
};
// แปลง string ที่อาจมี comma เช่น "1,234.56" → number
const parseNum = (v) => parseFloat(String(v).replace(/,/g, "")) || 0;

// ─── Price fetching (Bitkub THB + Binance USD) ───────────────────────────────
const fetchBitkubTHB = async () => {
  try {
    const r = await fetch(
      "https://api.bitkub.com/api/market/ticker?sym=THB_BTC"
    );
    const j = await r.json();
    return j?.THB_BTC?.last ?? null;
  } catch {
    return null;
  }
};

const fetchBinanceUSD = async () => {
  try {
    const r = await fetch(
      "https://api.binance.com/api/v3/ticker/price?symbol=BTCUSDT"
    );
    const j = await r.json();
    return j?.price ? parseFloat(j.price) : null;
  } catch {
    return null;
  }
};

// ดึงทั้งสองแหล่ง — return { thb, usd }
const fetchAllPrices = async () => {
  const [thb, usd] = await Promise.allSettled([
    fetchBitkubTHB(),
    fetchBinanceUSD(),
  ]);
  return {
    thb: thb.status === "fulfilled" ? thb.value : null,
    usd: usd.status === "fulfilled" ? usd.value : null,
  };
};

// ─── Enrich ───────────────────────────────────────────────────────────────────
function enrichEntries(entries) {
  let cumSat = 0,
    cumFiat = 0;
  return entries.map((e, i) => {
    const isSell = e.type === "Sell";
    if (isSell) {
      cumSat -= e.satoshi; // ขาย = หัก sat
      cumFiat -= e.fiat_thb; // รับเงินคืน = หัก cost basis
    } else {
      cumSat += e.satoshi;
      cumFiat += e.fiat_thb;
    }
    cumSat = Math.max(0, cumSat);
    cumFiat = Math.max(0, cumFiat);
    const portfolioValue = (cumSat / 1e8) * (e.price_thb || 0);
    const invested = cumFiat;
    const unrealized = portfolioValue - invested;
    const pctUnrealized = invested > 0 ? (unrealized / invested) * 100 : 0;
    const satPerTHB = e.price_thb > 0 ? 1e8 / e.price_thb : 0;
    return {
      ...e,
      dayActive: i + 1,
      cumSat,
      cumFiat,
      portfolioValue,
      invested,
      unrealized,
      pctUnrealized,
      satPerTHB,
    };
  });
}

function computeSummary(enriched, currentPrice, goals) {
  if (!enriched.length) return null;
  const spendFiat = enriched.reduce((s, e) => s + e.fiat_thb, 0);
  const totalSatoshi = enriched.reduce((s, e) => s + e.satoshi, 0);
  const numberOfDays = enriched.length;
  const averageCost = spendFiat > 0 ? totalSatoshi / spendFiat : 0;
  const todaySatPerTHB = currentPrice > 0 ? 1e8 / currentPrice : 0;
  const marketValue = (totalSatoshi / 1e8) * currentPrice;
  const pctProfitLoss =
    spendFiat > 0 ? ((marketValue - spendFiat) / spendFiat) * 100 : 0;
  let peak = 0,
    maxDrawdown = 0;
  for (const e of enriched) {
    if (e.portfolioValue > peak) peak = e.portfolioValue;
    if (peak > 0) {
      const dd = ((e.portfolioValue - peak) / peak) * 100;
      if (dd < maxDrawdown) maxDrawdown = dd;
    }
  }
  let worstPct = 0,
    worstThb = 0,
    worstDate = "",
    bestPct = 0,
    bestDate = "";
  for (const e of enriched) {
    const cv = (e.satoshi / 1e8) * currentPrice,
      diff = cv - e.fiat_thb,
      pct = e.fiat_thb > 0 ? (diff / e.fiat_thb) * 100 : 0;
    if (pct < worstPct) {
      worstPct = pct;
      worstThb = diff;
      worstDate = e.date;
    }
    if (pct > bestPct) {
      bestPct = pct;
      bestDate = e.date;
    }
  }
  return {
    spendFiat,
    totalSatoshi,
    numberOfDays,
    averageCost,
    todaySatPerTHB,
    marketValue,
    pctProfitLoss,
    maxDrawdown,
    worstPct,
    worstThb,
    worstDate,
    bestPct,
    bestDate,
    progressFiat:
      goals.goal_fiat > 0 ? (marketValue / goals.goal_fiat) * 100 : 0,
    progressBTC:
      goals.goal_satoshi > 0 ? (totalSatoshi / goals.goal_satoshi) * 100 : 0,
    currentPrice,
    goalFiat: goals.goal_fiat,
    goalSat: goals.goal_satoshi,
  };
}

function computeDelta24(enriched) {
  if (enriched.length < 2) return null;
  const last = enriched[enriched.length - 1],
    prev = enriched[enriched.length - 2];
  const delta = last.portfolioValue - prev.portfolioValue;
  return {
    delta,
    pct: prev.portfolioValue > 0 ? (delta / prev.portfolioValue) * 100 : 0,
  };
}

// ─── Sparkline ────────────────────────────────────────────────────────────────
function Sparkline({ values, color }) {
  if (!values || values.length < 2) return null;
  const w = 64,
    h = 20,
    min = Math.min(...values),
    max = Math.max(...values),
    range = max - min || 1;
  const pts = values
    .map(
      (v, i) =>
        `${((i / (values.length - 1)) * w).toFixed(1)},${(
          h -
          ((v - min) / range) * h
        ).toFixed(1)}`
    )
    .join(" ");
  return (
    <svg className="dca-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.3" />
    </svg>
  );
}

// ─── Chart ────────────────────────────────────────────────────────────────────
const CHART_MODES = ["portfolio", "pnl", "cost", "sats", "entries"];
const CHART_LABELS = {
  portfolio: "Portfolio vs Invested",
  pnl: "Unrealized P&L",
  cost: "Cost vs Market",
  sats: "Sat Accumulation",
  entries: "Entry Points",
};
const TIMEFRAMES = ["7D", "30D", "60D", "90D", "180D", "1Y", "2Y", "4Y", "ALL"];
const TF_DAYS = {
  "7D": 7,
  "30D": 30,
  "60D": 60,
  "90D": 90,
  "180D": 180,
  "1Y": 365,
  "2Y": 730,
  "4Y": 1460,
  ALL: Infinity,
};

function ChartSVG({ data, mode }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [dims, setDims] = useState({ w: 700, h: 260 });
  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(() => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setDims({ w: r.width, h: r.height });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const { w, h } = dims,
    padL = 52,
    padR = mode === "sats" ? 52 : 14,
    padT = 18,
    padB = 26;
  const cw = Math.max(0, w - padL - padR),
    ch = Math.max(0, h - padT - padB);
  const series = useMemo(() => {
    if (mode === "portfolio")
      return [
        {
          key: "portfolio",
          label: "Portfolio Value",
          color: "var(--accent)",
          fill: "var(--accent-line)",
          dash: null,
          zero: false,
          right: false,
          values: data.map((d) => d.portfolioValue),
        },
        {
          key: "invested",
          label: "Invested",
          color: "var(--fg-2)",
          fill: null,
          dash: "4 4",
          zero: false,
          right: false,
          values: data.map((d) => d.invested),
        },
      ];
    if (mode === "pnl")
      return [
        {
          key: "pnl",
          label: "Unrealized PNL",
          color: "var(--accent)",
          fill: "var(--accent-line)",
          dash: null,
          zero: true,
          right: false,
          values: data.map((d) => d.unrealized),
        },
      ];
    if (mode === "sats")
      return [
        {
          key: "sats",
          label: "Cumulative Sat",
          color: "var(--accent)",
          fill: "var(--accent-line)",
          dash: null,
          zero: false,
          right: false,
          values: data.map((d) => d.cumSat),
        },
        {
          key: "pthb",
          label: "Portfolio (฿)",
          color: "var(--fg)",
          fill: null,
          dash: null,
          zero: false,
          right: true,
          values: data.map((d) => d.portfolioValue),
        },
        {
          key: "ithb",
          label: "Invested (฿)",
          color: "var(--fg)",
          fill: null,
          dash: "4 4",
          zero: false,
          right: true,
          values: data.map((d) => d.invested),
        },
      ];
    if (mode === "entries")
      return [
        {
          key: "price",
          label: "BTC Price",
          color: "var(--muted)",
          fill: null,
          dash: null,
          zero: false,
          right: false,
          values: data.map((d) => d.price_thb),
        },
        {
          key: "cost",
          label: "Avg Cost Basis",
          color: "var(--fg-2)",
          fill: null,
          dash: "4 4",
          zero: false,
          right: false,
          values: data.map((_, i) => {
            const s = data.slice(0, i + 1),
              ts = s.reduce((a, r) => a + r.satoshi, 0),
              tf = s.reduce((a, r) => a + r.fiat_thb, 0);
            return tf / (ts / 1e8);
          }),
        },
      ];
    return [
      {
        key: "market",
        label: "Market Price",
        color: "var(--accent)",
        fill: null,
        dash: null,
        zero: false,
        right: false,
        values: data.map((d) => d.price_thb),
      },
      {
        key: "cost",
        label: "Avg Cost Basis",
        color: "var(--fg-2)",
        fill: null,
        dash: "4 4",
        zero: false,
        right: false,
        values: data.map((_, i) => {
          const s = data.slice(0, i + 1),
            ts = s.reduce((a, r) => a + r.satoshi, 0),
            tf = s.reduce((a, r) => a + r.fiat_thb, 0);
          return tf / (ts / 1e8);
        }),
      },
    ];
  }, [data, mode]);
  const lv = series.filter((s) => !s.right).flatMap((s) => s.values);
  let yMin = lv.length ? Math.min(...lv) : 0,
    yMax = lv.length ? Math.max(...lv) : 1;
  if (series.find((s) => s.zero)) {
    yMin = Math.min(yMin, 0);
    yMax = Math.max(yMax, 0);
  }
  const yr = yMax - yMin || 1;
  yMin -= yr * 0.06;
  yMax += yr * 0.06;
  const rv = series.filter((s) => s.right).flatMap((s) => s.values);
  let yMinR = rv.length ? Math.min(...rv) : 0,
    yMaxR = rv.length ? Math.max(...rv) : 1;
  const yrR = yMaxR - yMinR || 1;
  yMinR -= yrR * 0.06;
  yMaxR += yrR * 0.06;
  const xf = (i) =>
    padL + (data.length <= 1 ? 0 : (i / (data.length - 1)) * cw);
  const yf = (v) => padT + ch - ((v - yMin) / (yMax - yMin)) * ch;
  const yfR = (v) => padT + ch - ((v - yMinR) / (yMaxR - yMinR)) * ch;
  const yFn = (s) => (s.right ? yfR : yf);
  const grid = Array.from({ length: 5 }, (_, i) => {
    const v = yMin + ((yMax - yMin) / 4) * i;
    return { v, y: yf(v) };
  });
  const gridR = Array.from({ length: 5 }, (_, i) => {
    const v = yMinR + ((yMaxR - yMinR) / 4) * i;
    return { v, y: yfR(v) };
  });
  const zeroY = yMin < 0 && yMax > 0 ? yf(0) : null;
  const fa = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e6) return (v / 1e6).toFixed(1) + "M";
    if (abs >= 1000) return Math.round(v / 1000) + "K";
    return v.toFixed(0);
  };
  const bp = (vals, area, fn) => {
    if (!vals.length) return "";
    let d = `M ${xf(0)} ${fn(vals[0])}`;
    for (let i = 1; i < vals.length; i++) d += ` L ${xf(i)} ${fn(vals[i])}`;
    if (area) {
      const by = zeroY != null ? zeroY : padT + ch;
      d += ` L ${xf(vals.length - 1)} ${by} L ${xf(0)} ${by} Z`;
    }
    return d;
  };
  const xTicks = Array.from({ length: Math.min(6, data.length) }, (_, i) => {
    const idx = Math.round(
      (i / (Math.min(6, data.length) - 1 || 1)) * (data.length - 1)
    );
    return { idx, x: xf(idx), date: data[idx]?.date };
  });
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect(),
      mx = e.clientX - rect.left;
    if (mx < padL) {
      setHoverIdx(null);
      return;
    }
    setHoverIdx(
      Math.max(
        0,
        Math.min(
          data.length - 1,
          Math.round(((mx - padL) / cw) * (data.length - 1))
        )
      )
    );
  };
  const hov = hoverIdx != null ? data[hoverIdx] : null;
  let trows = [];
  if (hov) {
    if (mode === "portfolio")
      trows = [
        { l: "Portfolio", v: fmtThb(hov.portfolioValue) + " ฿" },
        { l: "Invested", v: fmtThb(hov.invested) + " ฿" },
        {
          l: "Unrealized",
          v: (hov.unrealized >= 0 ? "+" : "") + fmtThb(hov.unrealized) + " ฿",
        },
      ];
    else if (mode === "pnl")
      trows = [
        {
          l: "Unrealized",
          v: (hov.unrealized >= 0 ? "+" : "") + fmtThb(hov.unrealized) + " ฿",
        },
        { l: "%", v: hov.pctUnrealized.toFixed(2) + "%" },
        { l: "Portfolio", v: fmtThb(hov.portfolioValue) + " ฿" },
      ];
    else if (mode === "sats")
      trows = [
        { l: "Cumulative", v: hov.cumSat.toLocaleString("en-US") + " sat" },
        { l: "Buy", v: hov.satoshi.toLocaleString("en-US") + " sat" },
        { l: "Portfolio", v: fmtThb(hov.portfolioValue) + " ฿" },
      ];
    else if (mode === "entries")
      trows = [
        { l: "BTC Price", v: fmtThb(hov.price_thb) + " ฿" },
        { l: "Bought", v: hov.satoshi.toLocaleString("en-US") + " sat" },
        { l: "Spent", v: hov.fiat_thb + " ฿" },
      ];
    else {
      const sl = hoverIdx != null ? data.slice(0, hoverIdx + 1) : [];
      const ts = sl.reduce((a, r) => a + r.satoshi, 0),
        tf = sl.reduce((a, r) => a + r.fiat_thb, 0),
        cb = tf / (ts / 1e8);
      trows = [
        { l: "Market", v: fmtThb(hov.price_thb) + " ฿" },
        { l: "Avg Cost", v: fmtThb(cb) + " ฿" },
        {
          l: "Spread",
          v: (((hov.price_thb - cb) / cb) * 100).toFixed(2) + "%",
        },
      ];
    }
  }
  const tleft = hoverIdx != null ? xf(hoverIdx) : 0;
  const legend = series.map((s) => (
    <span className="dca-legend-item" key={s.key}>
      <span
        className="dca-swatch"
        style={{
          background: s.dash ? "transparent" : s.color,
          border: s.dash ? `2px dashed ${s.color}` : "none",
          height: s.dash ? 0 : 10,
        }}
      />
      {s.label}
    </span>
  ));
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="dca-chart-legend">{legend}</div>
      <div
        ref={wrapRef}
        style={{ flex: 1, position: "relative", minHeight: 0 }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <svg
          width={w}
          height={h}
          style={{ display: "block" }}
          onMouseMove={onMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {grid.map((g, i) => (
            <g key={i}>
              <line
                x1={padL}
                x2={w - padR}
                y1={g.y}
                y2={g.y}
                stroke="var(--divider)"
                strokeWidth="1"
              />
              <text
                x={padL - 8}
                y={g.y + 3}
                textAnchor="end"
                fontSize="10"
                fontFamily="var(--mono)"
                fill="var(--muted)"
              >
                {fa(g.v)}
              </text>
            </g>
          ))}
          {mode === "sats" &&
            gridR.map((g, i) => (
              <text
                key={"r" + i}
                x={w - padR + 8}
                y={g.y + 3}
                textAnchor="start"
                fontSize="10"
                fontFamily="var(--mono)"
                fill="var(--muted)"
              >
                {fa(g.v)}
              </text>
            ))}
          {zeroY != null && (
            <line
              x1={padL}
              x2={w - padR}
              y1={zeroY}
              y2={zeroY}
              stroke="var(--muted)"
              strokeWidth="1"
              strokeDasharray="2 3"
              opacity="0.5"
            />
          )}
          {xTicks.map((t, i) => (
            <text
              key={i}
              x={t.x}
              y={h - 8}
              textAnchor={
                i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"
              }
              fontSize="10"
              fontFamily="var(--mono)"
              fill="var(--muted)"
            >
              {t.date ? fmtDateShort(t.date) : ""}
            </text>
          ))}
          {series.map(
            (s) =>
              s.fill && (
                <path
                  key={s.key + "-a"}
                  d={bp(s.values, true, yFn(s))}
                  fill={s.fill}
                  stroke="none"
                />
              )
          )}
          {series.map((s) => (
            <path
              key={s.key + "-l"}
              d={bp(s.values, false, yFn(s))}
              fill="none"
              stroke={s.color}
              strokeWidth="1.75"
              strokeDasharray={s.dash || "none"}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          {mode === "entries" &&
            data.map((d, i) => (
              <circle
                key={i}
                cx={xf(i)}
                cy={yf(d.price_thb)}
                r="3.5"
                fill="var(--accent)"
                opacity="0.7"
              />
            ))}
          {hoverIdx != null && (
            <g>
              <line
                x1={xf(hoverIdx)}
                x2={xf(hoverIdx)}
                y1={padT}
                y2={padT + ch}
                stroke="var(--fg)"
                strokeWidth="1"
                strokeDasharray="2 3"
                opacity="0.4"
              />
              {series.map((s) => (
                <circle
                  key={s.key + "-d"}
                  cx={xf(hoverIdx)}
                  cy={yFn(s)(s.values[hoverIdx] ?? 0)}
                  r="4"
                  fill="var(--surface)"
                  stroke={s.color}
                  strokeWidth="2"
                />
              ))}
            </g>
          )}
        </svg>
        {hov && hoverIdx != null && (
          <div
            className="dca-tooltip"
            style={{
              left: tleft,
              top: 0,
              transform:
                tleft > padL + cw * 0.65
                  ? "translate(-100%,-100%)"
                  : "translate(-50%,-100%)",
            }}
          >
            <div className="dca-t-date">
              {fmtDate(hov.date)} · Day {hov.dayActive}
            </div>
            {trows.map((r, i) => (
              <div className="dca-t-row" key={i}>
                <span className="dca-t-lbl">{r.l}</span>
                <span>{r.v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add Buy Modal ────────────────────────────────────────────────────────────
const PLATFORMS = [
  "Bitkub",
  "Binance",
  "Coinbase",
  "OKX",
  "Kraken",
  "Gate.io",
  "อื่นๆ",
];

function AddBuyModal({ onClose, prices, onSaved, lastEntry }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [fiat, setFiat] = useState("");
  const [currency, setCurrency] = useState("THB"); // THB หรือ USD
  const [priceInput, setPriceInput] = useState(""); // ราคาในสกุลที่เลือก
  const [satsInput, setSatsInput] = useState(""); // ใส่เป็น sat หรือ BTC ก็ได้
  const [satsMode, setSatsMode] = useState("sat"); // "sat" หรือ "btc"
  const [satsManual, setSatsManual] = useState(false);
  const [txType, setTxType] = useState("Buy");
  const [platform, setPlatform] = useState("Bitkub");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // USD→THB rate โดยประมาณ (ดึงจาก prices ถ้ามีทั้งสอง)
  const usdToThb =
    prices.thb && prices.usd && prices.usd > 0 ? prices.thb / prices.usd : 34.5; // fallback

  // ราคา BTC ในหน่วย THB (สำหรับ auto-compute sats)
  const priceTHB = (() => {
    const p = parseFloat(priceInput);
    if (!p) return 0;
    return currency === "USD" ? p * usdToThb : p;
  })();

  // sats จาก input (รองรับทั้ง sat และ BTC decimal เช่น 0.0068)
  const satsFromInput = (() => {
    const v = parseFloat(satsInput);
    if (!v) return 0;
    if (satsMode === "btc") return v * 1e8; // 0.0068 BTC → 680000 sat
    return v; // ใส่ตรงๆ เป็น sat
  })();

  // auto-compute sats เมื่อ fiat/price เปลี่ยน
  useEffect(() => {
    if (satsManual) return;
    const f = parseFloat(fiat);
    if (f > 0 && priceTHB > 0) {
      const computed = (f / priceTHB) * 1e8;
      setSatsInput(
        satsMode === "btc" ? (computed / 1e8).toFixed(8) : computed.toFixed(2)
      );
    } else {
      setSatsInput("");
    }
  }, [fiat, priceInput, currency, satsMode, satsManual]);

  // เมื่อเปลี่ยน satsMode ให้แปลงค่าที่มีอยู่
  function handleSatsModeChange(newMode) {
    const cur = parseFloat(satsInput) || 0;
    if (cur > 0) {
      if (newMode === "btc" && satsMode === "sat")
        setSatsInput((cur / 1e8).toFixed(8));
      if (newMode === "sat" && satsMode === "btc")
        setSatsInput((cur * 1e8).toFixed(2));
    }
    setSatsMode(newMode);
  }

  // ใส่ราคา live
  function fillLivePrice() {
    if (currency === "THB" && prices.thb) {
      setPriceInput(String(Math.round(prices.thb)));
      setSatsManual(false);
    }
    if (currency === "USD" && prices.usd) {
      setPriceInput(prices.usd.toFixed(2));
      setSatsManual(false);
    }
  }
  const livePrice = currency === "THB" ? prices.thb : prices.usd;
  const livePriceLabel =
    currency === "THB"
      ? prices.thb
        ? `฿${fmtInt(prices.thb)}`
        : null
      : prices.usd
      ? `$${prices.usd.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
      : null;

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    const fiatNum = parseFloat(fiat) || 0;
    if (!date || !fiatNum || !satsFromInput) {
      setError("กรอก Date, Fiat และ Satoshi/BTC ให้ครบ");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSaved({
        date,
        type: txType,
        fiat_thb: fiatNum, // เก็บ THB เสมอ (ใช้คำนวณ portfolio)
        fiat_original: fiatNum, // ยอดที่กรอกจริง
        currency, // "THB" หรือ "USD"
        satoshi: satsFromInput,
        price_thb: priceTHB,
        platform,
        note,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Error");
    }
    setBusy(false);
  }

  const fiatNum = parseFloat(fiat) || 0;

  return (
    <div className="dca-modal-backdrop" onClick={onClose}>
      <form
        className="dca-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="dca-modal-head">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                display: "flex",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                overflow: "hidden",
              }}
            >
              {["Buy", "Sell"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTxType(t)}
                  style={{
                    padding: "5px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                    background:
                      txType === t
                        ? t === "Buy"
                          ? "var(--pos)"
                          : "var(--neg)"
                        : "var(--surface-2)",
                    color: txType === t ? "#fff" : "var(--muted)",
                  }}
                >
                  {t === "Buy" ? "▲ Buy" : "▼ Sell"}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="dca-btn"
            onClick={onClose}
            style={{ padding: "2px 8px" }}
          >
            ✕
          </button>
        </div>
        <div className="dca-modal-body">
          {/* ยอดสะสมล่าสุด */}
          {lastEntry && (
            <div
              style={{
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                padding: "8px 12px",
                fontFamily: "var(--mono)",
                fontSize: 11,
                color: "var(--muted)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>Last · {fmtDateShort(lastEntry.date)}</span>
              <span>
                +{fmtInt(lastEntry.satoshi)} sat → สะสม{" "}
                <strong style={{ color: "var(--accent)" }}>
                  {fmtInt(lastEntry.cumSat)} sat
                </strong>
              </span>
            </div>
          )}

          <div className="dca-field">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          {/* Fiat + Currency toggle */}
          <div className="dca-field">
            <label
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Fiat spent</span>
              <div
                style={{
                  display: "flex",
                  border: "1px solid var(--border)",
                  borderRadius: 3,
                  overflow: "hidden",
                  fontFamily: "var(--mono)",
                }}
              >
                {["THB", "USD"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    style={{
                      padding: "2px 8px",
                      fontSize: 10,
                      border: "none",
                      cursor: "pointer",
                      background:
                        currency === c ? "var(--fg)" : "var(--surface-2)",
                      color: currency === c ? "var(--bg)" : "var(--muted)",
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </label>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                  color: "var(--muted)",
                  pointerEvents: "none",
                }}
              >
                {currency === "THB" ? "฿" : "$"}
              </span>
              <input
                type="number"
                value={fiat}
                min={0}
                step="any"
                placeholder={currency === "THB" ? "108" : "3"}
                style={{ paddingLeft: 24 }}
                onChange={(e) => setFiat(e.target.value)}
                required
              />
            </div>
          </div>

          {/* BTC Price */}
          <div className="dca-field">
            <label
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                BTC price ({currency}){" "}
                <span
                  style={{
                    color: "var(--muted-2)",
                    fontWeight: 400,
                    fontSize: 10,
                    textTransform: "none",
                  }}
                >
                  optional
                </span>
              </span>
              {livePriceLabel && (
                <button
                  type="button"
                  onClick={fillLivePrice}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "var(--mono)",
                    fontSize: 10,
                    color: "var(--accent)",
                    padding: 0,
                  }}
                >
                  LIVE {livePriceLabel}
                </button>
              )}
            </label>
            <input
              type="number"
              value={priceInput}
              min={0}
              step="any"
              placeholder={currency === "THB" ? "เช่น 2800000" : "เช่น 84000"}
              onChange={(e) => {
                setPriceInput(e.target.value);
                setSatsManual(false);
              }}
            />
            {priceTHB > 0 && currency === "USD" && (
              <span
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 10,
                  color: "var(--muted)",
                  marginTop: 2,
                }}
              >
                ≈ ฿{fmtInt(priceTHB)} (rate {usdToThb.toFixed(2)} ฿/$)
              </span>
            )}
          </div>

          {/* Satoshi/BTC input */}
          <div className="dca-field">
            <label
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Amount received</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {satsManual && (
                  <button
                    type="button"
                    onClick={() => {
                      setSatsManual(false);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "var(--mono)",
                      fontSize: 10,
                      color: "var(--muted)",
                      padding: 0,
                    }}
                  >
                    ↺ auto
                  </button>
                )}
                {!satsManual && (
                  <span
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: 10,
                      color: "var(--pos)",
                    }}
                  >
                    ✦ auto
                  </span>
                )}
                <div
                  style={{
                    display: "flex",
                    border: "1px solid var(--border)",
                    borderRadius: 3,
                    overflow: "hidden",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {["sat", "btc"].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleSatsModeChange(m)}
                      style={{
                        padding: "2px 8px",
                        fontSize: 10,
                        border: "none",
                        cursor: "pointer",
                        background:
                          satsMode === m ? "var(--fg)" : "var(--surface-2)",
                        color: satsMode === m ? "var(--bg)" : "var(--muted)",
                      }}
                    >
                      {m.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </label>
            <input
              type="number"
              value={satsInput}
              min={0}
              step={satsMode === "btc" ? "0.00000001" : "0.01"}
              placeholder={
                satsMode === "btc"
                  ? "เช่น 0.0068 หรือ 0.00004509"
                  : "เช่น 4509.5"
              }
              style={{ color: "var(--accent)", fontWeight: 600 }}
              onChange={(e) => {
                setSatsInput(e.target.value);
                setSatsManual(true);
              }}
              required
            />
            {satsFromInput > 0 && (
              <span
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 10,
                  color: "var(--muted)",
                  marginTop: 2,
                }}
              >
                {satsMode === "btc"
                  ? `= ${fmtInt(satsFromInput)} sat`
                  : `= ${(satsFromInput / 1e8).toFixed(8)} BTC`}
                {fiatNum > 0 &&
                  ` · ${(satsFromInput / fiatNum).toFixed(2)} sat/฿`}
              </span>
            )}
          </div>

          <div className="dca-field">
            <label>Platform / Exchange</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="dca-field">
            <label>
              Note{" "}
              <span
                style={{
                  color: "var(--muted-2)",
                  fontWeight: 400,
                  fontSize: 10,
                  textTransform: "none",
                }}
              >
                optional
              </span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="เช่น DCA รายวัน, ซื้อเพิ่มช่วง dip..."
            />
          </div>
          {error && <div className="dca-err">{error}</div>}
        </div>
        <div className="dca-modal-foot">
          <button type="button" className="dca-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="dca-btn dca-btn-primary"
            disabled={busy}
          >
            {busy ? "Saving…" : "Record buy"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Records Table ────────────────────────────────────────────────────────────
const COLS = [
  {
    key: "dayActive",
    label: "Day",
    left: true,
    fmt: (v) => <span className="dca-day-chip">{v}</span>,
  },
  {
    key: "type",
    label: "Type",
    left: true,
    fmt: (v, r) => (
      <span
        style={{
          fontFamily: "var(--mono)",
          fontSize: 10,
          padding: "1px 6px",
          borderRadius: 3,
          background: !v || v === "Buy" ? "var(--pos-soft)" : "var(--neg-soft)",
          color: !v || v === "Buy" ? "var(--pos)" : "var(--neg)",
        }}
      >
        {v || "Buy"}
      </span>
    ),
  },
  { key: "date", label: "Date", left: true, fmt: (v) => fmtDate(v) },
  {
    key: "fiat_thb",
    label: "Fiat",
    left: false,
    fmt: (v, r) => {
      const cur = r.currency || "THB";
      const orig = r.fiat_original || v;
      if (cur === "USD")
        return (
          <span>
            <span style={{ color: "var(--fg)" }}>
              $
              {Number(orig).toLocaleString("en-US", {
                maximumFractionDigits: 2,
              })}
            </span>
            <span style={{ color: "var(--muted)", fontSize: 10 }}>
              {" "}
              / ฿{fmtInt(v)}
            </span>
          </span>
        );
      return <span>฿{fmtInt(v)}</span>;
    },
  },
  {
    key: "satoshi",
    label: "Satoshi",
    left: false,
    fmt: (v, r) => (
      <span
        style={{
          color: !r.type || r.type === "Buy" ? "var(--accent)" : "var(--neg)",
        }}
      >
        {!r.type || r.type === "Buy" ? "+" : "-"}
        {fmtInt(v)}
      </span>
    ),
  },
  {
    key: "price_thb",
    label: "BTC Price",
    left: false,
    fmt: (v) => (v ? fmtInt(v) : "—"),
  },
  {
    key: "platform",
    label: "Platform",
    left: false,
    fmt: (v) => (v ? <span className="dca-platform-badge">{v}</span> : "—"),
  },
  {
    key: "portfolioValue",
    label: "Portfolio Value",
    left: false,
    fmt: (v) => fmtThb(v),
  },
  { key: "invested", label: "Invested", left: false, fmt: (v) => fmtInt(v) },
  {
    key: "unrealized",
    label: "Unrealized",
    left: false,
    fmt: (v) => (v >= 0 ? "+" : "") + fmtThb(v),
    cls: (v) => (v >= 0 ? "pos" : "neg"),
  },
  {
    key: "pctUnrealized",
    label: "% Unrealized",
    left: false,
    fmt: (v) => (v >= 0 ? "+" : "") + Number(v).toFixed(2) + "%",
    cls: (v) => (v >= 0 ? "pos" : "neg"),
  },
  {
    key: "note",
    label: "Note",
    left: true,
    fmt: (v) =>
      v ? (
        <span
          style={{
            color: "var(--muted)",
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "inline-block",
          }}
        >
          {v}
        </span>
      ) : (
        ""
      ),
  },
];

function RecordsTable({ records, onDelete, onEdit }) {
  const [sortKey, setSortKey] = useState("dayActive");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [query, setQuery] = useState("");
  const [editId, setEditId] = useState(null);
  const [editFiat, setEditFiat] = useState(0);
  const [editSat, setEditSat] = useState(0);
  const [editPrice, setEditPrice] = useState(0);
  const [editType, setEditType] = useState("Buy");
  const [busyId, setBusyId] = useState(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return records;
    const q = query.toLowerCase();
    return records.filter(
      (r) =>
        String(r.dayActive).includes(q) ||
        fmtDate(r.date).toLowerCase().includes(q) ||
        String(r.price_thb).includes(q) ||
        (r.platform || "").toLowerCase().includes(q) ||
        (r.note || "").toLowerCase().includes(q)
    );
  }, [records, query]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey],
        bv = b[sortKey];
      if (typeof av === "number" && typeof bv === "number")
        return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageData = sorted.slice((curPage - 1) * pageSize, curPage * pageSize);
  useEffect(() => setPage(1), [pageSize, query, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const pageNums = () => {
    if (totalPages <= 7) {
      const a = [];
      for (let i = 1; i <= totalPages; i++) a.push(i);
      return a;
    }
    if (curPage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (curPage >= totalPages - 3)
      return [
        1,
        "…",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [1, "…", curPage - 1, curPage, curPage + 1, "…", totalPages];
  };

  async function saveEdit(r) {
    setBusyId(r.id);
    try {
      await onEdit(r.id, {
        fiat_thb: editFiat,
        price_thb: editPrice,
        satoshi: editSat,
        type: editType,
      });
      setEditId(null);
    } catch (e) {
      alert(e.message || "Error");
    }
    setBusyId(null);
  }
  async function deleteRow(r) {
    if (!window.confirm(`Delete entry for ${fmtDate(r.date)}?`)) return;
    setBusyId(r.id);
    try {
      await onDelete(r.id);
    } catch (e) {
      alert(e.message || "Error");
    }
    setBusyId(null);
  }

  return (
    <div className="dca-records">
      <div className="dca-records-toolbar">
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div className="dca-search">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <circle
                cx="7"
                cy="7"
                r="4.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M10.5 10.5l3 3"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            <input
              placeholder="Search day, date, platform, note…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <span
            style={{
              color: "var(--muted)",
              fontFamily: "var(--mono)",
              fontSize: 12,
            }}
          >
            {filtered.length} records
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: "var(--muted)",
            fontFamily: "var(--mono)",
            fontSize: 12,
          }}
        >
          <span>Rows per page</span>
          <select
            className="dca-select"
            value={pageSize}
            onChange={(e) => setPageSize(+e.target.value)}
          >
            {[30, 50, 100, 250].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="dca-records-scroll">
        <table className="dca-table">
          <thead>
            <tr>
              {COLS.map((c) => (
                <th
                  key={String(c.key)}
                  className={sortKey === c.key ? " dca-sorted" : ""}
                  style={{ textAlign: c.left ? "left" : "right" }}
                  onClick={() => toggleSort(c.key)}
                >
                  {c.label}
                  <span className="dca-sort-arrow">
                    {sortKey === c.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                  </span>
                </th>
              ))}
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td
                  colSpan={COLS.length + 1}
                  style={{
                    color: "var(--muted)",
                    textAlign: "center",
                    padding: 24,
                  }}
                >
                  No records. Click "Record buy" to start.
                </td>
              </tr>
            ) : (
              pageData.map((r) => {
                const isEdit = editId === r.id,
                  busy = busyId === r.id;
                return (
                  <tr key={r.id}>
                    {COLS.map((c) => {
                      if (isEdit && c.key === "type")
                        return (
                          <td key="type" className="dca-row-edit">
                            <select
                              value={editType}
                              onChange={(e) => setEditType(e.target.value)}
                              style={{
                                fontFamily: "var(--mono)",
                                fontSize: 11,
                                padding: "3px 6px",
                                background: "var(--surface)",
                                border: "1px solid var(--accent)",
                                borderRadius: 4,
                                color: "var(--fg)",
                              }}
                            >
                              <option value="Buy">Buy</option>
                              <option value="Sell">Sell</option>
                            </select>
                          </td>
                        );
                      if (isEdit && c.key === "fiat_thb")
                        return (
                          <td key="fiat_thb" className="dca-row-edit">
                            <input
                              type="number"
                              value={editFiat}
                              min={0}
                              step="any"
                              onChange={(e) => setEditFiat(+e.target.value)}
                            />
                          </td>
                        );
                      if (isEdit && c.key === "satoshi")
                        return (
                          <td key="satoshi" className="dca-row-edit">
                            <input
                              type="number"
                              value={editSat}
                              min={0}
                              step="any"
                              onChange={(e) => setEditSat(+e.target.value)}
                            />
                          </td>
                        );
                      if (isEdit && c.key === "price_thb")
                        return (
                          <td key="price_thb" className="dca-row-edit">
                            <input
                              type="number"
                              value={editPrice}
                              min={0}
                              step="any"
                              onChange={(e) => setEditPrice(+e.target.value)}
                            />
                          </td>
                        );
                      if (
                        isEdit &&
                        (c.key === "portfolioValue" ||
                          c.key === "invested" ||
                          c.key === "unrealized" ||
                          c.key === "pctUnrealized" ||
                          c.key === "platform" ||
                          c.key === "note" ||
                          c.key === "dayActive" ||
                          c.key === "date")
                      ) {
                        const raw = r[c.key],
                          cls =
                            c.cls && typeof raw === "number" ? c.cls(raw) : "";
                        return (
                          <td
                            key={String(c.key)}
                            style={{
                              textAlign: c.left ? "left" : "right",
                              opacity: 0.4,
                            }}
                            className={cls}
                          >
                            {c.fmt(raw, r)}
                          </td>
                        );
                      }
                      const raw = r[c.key],
                        cls =
                          c.cls && typeof raw === "number" ? c.cls(raw) : "";
                      return (
                        <td
                          key={String(c.key)}
                          style={{ textAlign: c.left ? "left" : "right" }}
                          className={cls}
                        >
                          {c.fmt(raw, r)}
                        </td>
                      );
                    })}
                    <td className="dca-row-actions">
                      {isEdit ? (
                        <>
                          <button
                            onClick={() => saveEdit(r)}
                            disabled={busy}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditId(null)}
                            disabled={busy}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setEditId(r.id);
                              setEditFiat(r.fiat_thb);
                              setEditPrice(r.price_thb);
                              setEditSat(r.satoshi);
                              setEditType(r.type || "Buy");
                            }}
                            title="Edit"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M11.5 2.5l2 2L5 13l-3 1 1-3 8.5-8.5z"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <button
                            className="danger"
                            onClick={() => deleteRow(r)}
                            title="Delete"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M3 4h10M6 4V2.5h4V4M4.5 4v10h7V4"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="dca-pagination">
        <span>
          {sorted.length === 0
            ? "Showing 0 of 0"
            : `Showing ${(curPage - 1) * pageSize + 1}–${Math.min(
                curPage * pageSize,
                sorted.length
              )} of ${sorted.length}`}
        </span>
        <div className="dca-pager">
          <button disabled={curPage === 1} onClick={() => setPage(1)}>
            «
          </button>
          <button disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>
            ‹
          </button>
          {pageNums().map((n, i) =>
            n === "…" ? (
              <button
                key={i}
                disabled
                style={{ border: "none", background: "transparent" }}
              >
                …
              </button>
            ) : (
              <button
                key={i}
                className={n === curPage ? "active" : ""}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            )
          )}
          <button
            disabled={curPage === totalPages}
            onClick={() => setPage(curPage + 1)}
          >
            ›
          </button>
          <button
            disabled={curPage === totalPages}
            onClick={() => setPage(totalPages)}
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function DCAPage({ apiUrl }) {
  const [rawEntries, setRawEntries] = useState([]);
  const [goals, setGoals] = useState({
    goal_fiat: 200000,
    goal_satoshi: 2000000,
  });
  const [prices, setPrices] = useState({ thb: null, usd: null });
  const [priceStale, setPriceStale] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [chartMode, setChartMode] = useState("portfolio");
  const [timeframe, setTimeframe] = useState("ALL");
  const [editGoalField, setEditGoalField] = useState(null);
  const [goalDraft, setGoalDraft] = useState(0);
  const intervalRef = useRef(null);

  // ── fetch ───────────────────────────────────────────────────────────────────
  const refreshData = useCallback(async () => {
    setDataLoading(true);
    try {
      const res = await fetch(`${apiUrl}?action=getDCAData`);
      const json = await res.json();
      console.log("DCA API raw response:", JSON.stringify(json).slice(0, 500));
      if (json.status === "success" && json.data) {
        // map: API ส่ง { id, date, fiat, sats, price, platform, note }
        // แปลงเป็น { fiat_thb, satoshi, price_thb } ที่ enrichEntries ใช้
        // ใช้ parseNum เพื่อรองรับ string ที่มี comma
        const entries = (json.data.entries || [])
          .map((e) => ({
            ...e,
            fiat_thb: parseNum(e.fiat),
            fiat_original: parseNum(e.fiatOriginal || e.fiat),
            currency: e.currency || "THB",
            satoshi: parseNum(e.sats),
            price_thb: parseNum(e.price),
            platform: e.platform || "",
            note: e.note || "",
          }))
          .filter((e) => e.fiat_thb > 0 && e.satoshi > 0 && e.price_thb > 0) // กรองแถวว่าง
          .sort((a, b) => a.date.localeCompare(b.date)); // sort ASC
        setRawEntries(entries);
        const g = json.data.goals || {};
        setGoals({
          goal_fiat: parseNum(g.fiat) || 200000,
          goal_satoshi: parseNum(g.sats) || 2000000,
        });
      }
    } catch (e) { console.error(e); }
    setDataLoading(false);
  }, [apiUrl]);

  useEffect(() => { refreshData(); }, [refreshData]);

  // ── price ───────────────────────────────────────────────────────────────────
  const refreshPrice = useCallback(async () => {
    const p = await fetchAllPrices();
    if (p.thb || p.usd) {
      setPrices(p);
      setPriceStale(false);
    } else {
      setPriceStale(true);
    }
  }, []);

  useEffect(() => {
    refreshPrice();
    intervalRef.current = setInterval(refreshPrice, 60_000);
    return () => clearInterval(intervalRef.current);
  }, [refreshPrice]);

  // ── derived ─────────────────────────────────────────────────────────────────
  const enriched = useMemo(() => enrichEntries(rawEntries), [rawEntries]);
  const summary = useMemo(
    () => computeSummary(enriched, prices.thb || 0, goals),
    [enriched, prices.thb, goals]
  );
  const delta24 = useMemo(() => computeDelta24(enriched), [enriched]);
  const chartData = useMemo(() => {
    const n = TF_DAYS[timeframe] || Infinity;
    return n === Infinity ? enriched : enriched.slice(-n);
  }, [enriched, timeframe]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleAdd = async (entry) => {
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({
        action: "addDCAEntry",
        date: entry.date,
        type: entry.type || "Buy",
        fiat: entry.fiat_thb,
        fiatOriginal: entry.fiat_original || entry.fiat_thb,
        currency: entry.currency || "THB",
        sats: entry.satoshi,
        price: entry.price_thb,
        platform: entry.platform,
        note: entry.note,
      }),
    });
    await refreshData();
  };

  const handleDelete = async (id) => {
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({ action: "deleteDCAEntry", id }),
    });
    await refreshData();
  };

  const handleEdit = async (id, data) => {
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({
        action: "editDCAEntry",
        id,
        type: data.type || "Buy",
        fiat: data.fiat_thb,
        sats: data.satoshi,
        price: data.price_thb,
      }),
    });
    await refreshData();
  };

  const saveGoal = async (field) => {
    const val = Math.floor(goalDraft);
    if (!val || val <= 0) return;
    const newGoals = { ...goals, [field]: val };
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({
        action: "saveDCAGoals",
        fiat: newGoals.goal_fiat,
        sats: newGoals.goal_satoshi,
      }),
    });
    setGoals(newGoals);
    setEditGoalField(null);
  };

  // ── stats cells ─────────────────────────────────────────────────────────────
  const last30 = enriched.slice(-30);
  const statCells = summary
    ? [
        {
          lbl: "Spend Fiat",
          val: fmtInt(summary.spendFiat),
          sub: "฿",
          foot: `${summary.numberOfDays} days · ${
            summary.numberOfDays > 0
              ? Math.round(summary.spendFiat / summary.numberOfDays)
              : 0
          } ฿/day`,
          color: "var(--fg)",
          spark: last30.map((r) => r.invested),
        },
        {
          lbl: "Total Satoshi",
          val: fmtInt(summary.totalSatoshi),
          sub: "sat",
          foot: (summary.totalSatoshi / 1e8).toFixed(8) + " BTC",
          color: "var(--accent)",
          spark: (() => {
            let s = 0;
            return last30.map((r) => ((s += r.satoshi), s));
          })(),
        },
        {
          lbl: "Current BTC Price",
          val: fmtInt(summary.currentPrice),
          sub: "฿",
          foot: "Bitkub · spot",
          color: "var(--accent)",
          spark: last30.map((r) => r.price_thb),
        },
        {
          lbl: "Market Value",
          val: fmtThb(summary.marketValue),
          sub: "฿",
          foot: "Your portfolio in THB",
          color: summary.pctProfitLoss >= 0 ? "var(--pos)" : "var(--neg)",
          spark: last30.map((r) => r.portfolioValue),
        },
        {
          lbl: "Average Cost",
          val: summary.averageCost.toFixed(2),
          sub: "sat/฿",
          foot: "Across all buys",
          color: "var(--fg)",
          spark: null,
        },
        {
          lbl: "Today sat/THB",
          val: summary.todaySatPerTHB.toFixed(2),
          sub: "sat/฿",
          foot:
            summary.todaySatPerTHB > summary.averageCost
              ? `+${(summary.todaySatPerTHB - summary.averageCost).toFixed(
                  2
                )} vs avg`
              : `${(summary.todaySatPerTHB - summary.averageCost).toFixed(
                  2
                )} vs avg`,
          color:
            summary.todaySatPerTHB > summary.averageCost
              ? "var(--pos)"
              : "var(--neg)",
          spark: last30.map((r) => r.satPerTHB),
        },
        {
          lbl: "Max Drawdown",
          val: summary.maxDrawdown.toFixed(2),
          sub: "%",
          foot: "Peak-to-trough",
          color: "var(--neg)",
          spark: null,
        },
        {
          lbl: "% Profit / Loss",
          val:
            (summary.pctProfitLoss >= 0 ? "+" : "") +
            summary.pctProfitLoss.toFixed(2),
          sub: "%",
          foot: `฿${fmtThb(
            summary.marketValue - summary.spendFiat
          )} unrealized`,
          color: summary.pctProfitLoss >= 0 ? "var(--pos)" : "var(--neg)",
          spark: null,
        },
        {
          lbl: "Worst Single Buy",
          val: (summary.worstPct <= 0 ? "" : "+") + summary.worstPct.toFixed(2),
          sub: "%",
          foot: summary.worstDate
            ? `${summary.worstDate} · ฿${fmtThb(summary.worstThb)}`
            : "No losing entries",
          color: summary.worstPct < 0 ? "var(--neg)" : "var(--pos)",
          spark: null,
        },
        {
          lbl: "Best Single Buy",
          val: "+" + summary.bestPct.toFixed(2),
          sub: "%",
          foot: summary.bestDate || "—",
          color: "var(--pos)",
          spark: null,
        },
      ]
    : [];

  const pos = summary ? summary.marketValue - summary.spendFiat >= 0 : true;
  const spendFiat = summary?.spendFiat ?? 0,
    totalSat = summary?.totalSatoshi ?? 0;
  const progressFiat = summary?.progressFiat ?? 0,
    progressBTC = summary?.progressBTC ?? 0;

  return (
    <>
      <style>{CSS}</style>
      <div className="dca-root">
        {/* Topbar */}
        <div className="dca-topbar">
          <div className="dca-brand">
            <div className="dca-brand-mark">₿</div>
            <div>
              <div className="dca-brand-name">DCA Tracker</div>
              <div className="dca-brand-sub">sats · THB</div>
            </div>
          </div>
          <div className="dca-topbar-actions">
            <span
              style={{
                fontFamily: "var(--mono)",
                fontSize: 11,
                color: "var(--muted)",
                display: "flex",
                gap: 8,
              }}
            >
              {prices.thb && <span>฿{fmtInt(prices.thb)}</span>}
              {prices.usd && (
                <span style={{ color: "var(--muted-2)" }}>
                  / $
                  {prices.usd.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </span>
              )}
            </span>
            <button
              className="dca-btn dca-btn-primary"
              onClick={() => setShowAdd(true)}
            >
              + Add buy
            </button>
          </div>
        </div>

        {/* 01 Overview */}
        <div className="dca-section">
          <span className="dca-num-badge">01</span>
          <h2>Overview</h2>
          <span className="dca-dots" />
          <span className="dca-hint">PNL · chart · hover for daily values</span>
        </div>
        <div className="dca-hero">
          {/* PNL Card */}
          <div className="dca-pnl">
            <div className="dca-pnl-head">
              <span>Unrealized P&amp;L</span>
              <span className="dca-live">
                <span
                  className="dca-live-dot"
                  style={priceStale ? { background: "var(--muted)" } : {}}
                />{" "}
                LIVE
              </span>
            </div>
            <div>
              <div className="dca-pnl-value">
                <span className="dca-cur">฿</span>
                {summary
                  ? fmtThb(summary.marketValue - summary.spendFiat)
                  : "—"}
              </div>
              <div className="dca-pnl-delta">
                {summary && (
                  <span className={"dca-chip " + (pos ? "pos" : "neg")}>
                    {fmtPct(summary.pctProfitLoss)}
                  </span>
                )}
                {summary && (
                  <span
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: 11,
                      color: "var(--muted)",
                    }}
                  >
                    all-time · {summary.numberOfDays} days
                  </span>
                )}
              </div>
            </div>
            <div className="dca-pnl-split">
              <div>
                <div className="dca-lbl">Market Value</div>
                <div className="dca-val">
                  {summary ? `฿${fmtThb(summary.marketValue)}` : "—"}
                </div>
              </div>
              <div>
                <div className="dca-lbl">Invested</div>
                <div className="dca-val">
                  {summary ? `฿${fmtInt(summary.spendFiat)}` : "—"}
                </div>
              </div>
            </div>
            {delta24 && (
              <div className="dca-pnl-meta">
                24h{" "}
                <strong
                  style={{
                    color: delta24.delta >= 0 ? "var(--pos)" : "var(--neg)",
                  }}
                >
                  {delta24.delta >= 0 ? "+" : ""}
                  {fmtThb(delta24.delta)}฿
                </strong>{" "}
                ({fmtPct(delta24.pct)})
              </div>
            )}
          </div>
          {/* Chart Card */}
          <div className="dca-chart-card">
            <div className="dca-chart-head">
              <div className="dca-chart-tabs">
                {CHART_MODES.map((m) => (
                  <button
                    key={m}
                    className={
                      "dca-chart-tab" + (chartMode === m ? " active" : "")
                    }
                    onClick={() => setChartMode(m)}
                  >
                    {CHART_LABELS[m]}
                  </button>
                ))}
              </div>
              <div className="dca-tf-wrap">
                {TIMEFRAMES.map((t) => (
                  <button
                    key={t}
                    className={
                      "dca-tf-btn" + (timeframe === t ? " active" : "")
                    }
                    onClick={() => setTimeframe(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="dca-chart-body">
              {enriched.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "var(--muted)",
                    fontSize: 13,
                  }}
                >
                  Add your first buy to see chart
                </div>
              ) : (
                <ChartSVG data={chartData} mode={chartMode} />
              )}
            </div>
          </div>
        </div>

        {/* 02 Metrics & Goals */}
        <div className="dca-section">
          <span className="dca-num-badge">02</span>
          <h2>Metrics &amp; Goals</h2>
          <span className="dca-dots" />
          <span className="dca-hint">core numbers · progress</span>
        </div>
        {dataLoading && enriched.length === 0 ? (
          <div className="dca-spinner" />
        ) : (
          <>
            <div className="dca-stats">
              {statCells.map((s, i) => (
                <div className="dca-stat" key={i}>
                  <div className="dca-stat-lbl">{s.lbl}</div>
                  <div className="dca-stat-val" style={{ color: s.color }}>
                    {s.val}
                    <span className="dca-sub">{s.sub}</span>
                  </div>
                  <div className="dca-stat-foot">{s.foot}</div>
                  {s.spark && s.spark.length >= 2 && (
                    <Sparkline values={s.spark} color={s.color} />
                  )}
                </div>
              ))}
            </div>
            <div className="dca-goals">
              <div className="dca-goal">
                <div className="dca-goal-top">
                  <h3>Goal · Fiat Invested</h3>
                  <div
                    className="dca-goal-pct"
                    style={{ color: "var(--accent)" }}
                  >
                    {progressFiat.toFixed(2)}%
                  </div>
                </div>
                <div className="dca-goal-bar">
                  <div
                    className="dca-goal-fill"
                    style={{ width: Math.min(100, progressFiat) + "%" }}
                  />
                </div>
                <div className="dca-goal-ticks">
                  <span>฿{fmtInt(spendFiat)}</span>
                  {editGoalField === "goal_fiat" ? (
                    <span>
                      Goal · ฿
                      <input
                        type="number"
                        value={goalDraft}
                        min={1}
                        onChange={(e) => setGoalDraft(+e.target.value)}
                        onBlur={() => saveGoal("goal_fiat")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveGoal("goal_fiat");
                          if (e.key === "Escape") setEditGoalField(null);
                        }}
                        autoFocus
                      />
                    </span>
                  ) : (
                    <span
                      className="dca-editable"
                      onClick={() => {
                        setEditGoalField("goal_fiat");
                        setGoalDraft(goals.goal_fiat);
                      }}
                      title="Click to edit"
                    >
                      Goal · ฿{fmtInt(goals.goal_fiat)}
                    </span>
                  )}
                </div>
              </div>
              <div className="dca-goal">
                <div className="dca-goal-top">
                  <h3>Goal · Total Satoshi</h3>
                  <div className="dca-goal-pct">{progressBTC.toFixed(2)}%</div>
                </div>
                <div className="dca-goal-bar">
                  <div
                    className="dca-goal-fill alt"
                    style={{ width: Math.min(100, progressBTC) + "%" }}
                  />
                </div>
                <div className="dca-goal-ticks">
                  <span>{fmtInt(totalSat)} sat</span>
                  {editGoalField === "goal_satoshi" ? (
                    <span>
                      Goal ·{" "}
                      <input
                        type="number"
                        value={goalDraft}
                        min={1}
                        onChange={(e) => setGoalDraft(+e.target.value)}
                        onBlur={() => saveGoal("goal_satoshi")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveGoal("goal_satoshi");
                          if (e.key === "Escape") setEditGoalField(null);
                        }}
                        autoFocus
                      />{" "}
                      sat
                    </span>
                  ) : (
                    <span
                      className="dca-editable"
                      onClick={() => {
                        setEditGoalField("goal_satoshi");
                        setGoalDraft(goals.goal_satoshi);
                      }}
                      title="Click to edit"
                    >
                      Goal · {fmtInt(goals.goal_satoshi)} sat (
                      {(goals.goal_satoshi / 1e8).toFixed(2)} ₿)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* 03 Buy History */}
        <div className="dca-section">
          <span className="dca-num-badge">03</span>
          <h2>Buy History</h2>
          <span className="dca-dots" />
          <span className="dca-hint">sortable · searchable · paginated</span>
        </div>
        <RecordsTable
          records={enriched}
          onDelete={handleDelete}
          onEdit={handleEdit}
        />

        {showAdd && (
          <AddBuyModal
            onClose={() => setShowAdd(false)}
            prices={prices}
            onSaved={handleAdd}
            lastEntry={
              enriched.length > 0 ? enriched[enriched.length - 1] : null
            }
          />
        )}
      </div>
    </>
  );
}
